import { useCallback, useRef, useState } from 'react';
import { api } from '../api/client';
import type { OracleBlock, OracleBlockType, OracleEntityReference, OracleGraphNode, OracleGraphEdge, OracleSSEEvent } from '../../shared/types';

type UseOracleStreamReturn = {
  blocks: OracleBlock[];
  suggestions: string[];
  isStreaming: boolean;
  error: string | null;
  generate: (goal: string) => void;
  cancel: () => void;
  clear: () => void;
};

function parseEntitiesFromContent(content: string): OracleEntityReference[] {
  const entities: OracleEntityReference[] = [];
  const regex = /<entity\s+([^>]*)>([\s\S]*?)<\/entity>/g;
  let match;
  while ((match = regex.exec(content)) !== null) {
    const attrs = match[1];
    const summary = match[2].trim();
    const idMatch = attrs.match(/id="([^"]*)"/);
    const nameMatch = attrs.match(/name="([^"]*)"/);
    const typeMatch = attrs.match(/entity-type="([^"]*)"/);
    entities.push({
      id: idMatch?.[1] || '',
      name: nameMatch?.[1] || '',
      entityType: typeMatch?.[1] || '',
      summary,
    });
  }
  return entities;
}

function parseGraphData(content: string): { nodes: OracleGraphNode[]; edges: OracleGraphEdge[] } {
  const nodes: OracleGraphNode[] = [];
  const edges: OracleGraphEdge[] = [];

  // Parse <node ... /> elements
  const nodeRegex = /<node\s+([^>]*?)\/?\s*>/g;
  let match;
  while ((match = nodeRegex.exec(content)) !== null) {
    const attrs = match[1];
    const getAttr = (key: string) => {
      const m = attrs.match(new RegExp(`${key}="([^"]*)"`));
      return m?.[1] || '';
    };
    const id = getAttr('id');
    if (id) {
      nodes.push({
        id,
        label: getAttr('label') || id,
        group: getAttr('group') || 'neutral',
        entityId: getAttr('entity-id') || undefined,
        entityType: getAttr('entity-type') || undefined,
      });
    }
  }

  // Parse <edge ... /> elements
  const edgeRegex = /<edge\s+([^>]*?)\/?\s*>/g;
  while ((match = edgeRegex.exec(content)) !== null) {
    const attrs = match[1];
    const getAttr = (key: string) => {
      const m = attrs.match(new RegExp(`${key}="([^"]*)"`));
      return m?.[1] || '';
    };
    const from = getAttr('from');
    const to = getAttr('to');
    if (from && to) {
      edges.push({
        from,
        to,
        label: getAttr('label') || undefined,
        strength: (getAttr('strength') as OracleGraphEdge['strength']) || 'normal',
      });
    }
  }

  return { nodes, edges };
}

export function useOracleStream(): UseOracleStreamReturn {
  const [blocks, setBlocks] = useState<OracleBlock[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const clear = useCallback(() => {
    cancel();
    setBlocks([]);
    setSuggestions([]);
    setError(null);
  }, [cancel]);

  const generate = useCallback((goal: string) => {
    // Abort any existing stream
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    setBlocks([]);
    setSuggestions([]);
    setError(null);
    setIsStreaming(true);

    const handleEvent = (event: OracleSSEEvent) => {
      switch (event.event) {
        case 'block_start': {
          const newBlock: OracleBlock = {
            id: event.id,
            type: event.type as OracleBlockType,
            title: event.title,
            content: '',
            meta: event.meta || {},
            entities: [],
            isStreaming: true,
          };
          setBlocks((prev) => [...prev, newBlock]);
          break;
        }
        case 'block_content': {
          setBlocks((prev) =>
            prev.map((b) =>
              b.id === event.id ? { ...b, content: b.content + event.content } : b
            )
          );
          break;
        }
        case 'block_end': {
          setBlocks((prev) =>
            prev.map((b) => {
              if (b.id !== event.id) return b;
              const entities = parseEntitiesFromContent(b.content);
              const graphData = b.type === 'relationship-web' ? parseGraphData(b.content) : undefined;
              return { ...b, isStreaming: false, entities, graphData };
            })
          );
          break;
        }
        case 'suggestions': {
          setSuggestions(event.suggestions || []);
          break;
        }
        case 'error': {
          setError(event.message || 'Unknown error');
          setIsStreaming(false);
          break;
        }
        case 'done': {
          setIsStreaming(false);
          break;
        }
      }
    };

    api
      .streamOracleGenerate(goal, handleEvent, controller.signal)
      .then(() => {
        setIsStreaming(false);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') {
          // Cancelled by user, not an error
          return;
        }
        setError(err instanceof Error ? err.message : 'Stream failed');
        setIsStreaming(false);
      });
  }, []);

  return { blocks, suggestions, isStreaming, error, generate, cancel, clear };
}
